import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import axios from "axios";
import { Calendar, ArrowRight, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollReveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={className || ""}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blogs.php`).then((res) => {
      setBlogs(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Knowledge Hub
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-[#0B1F3A] mb-4">
              Financial Insights & Tips
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Expert articles to help you make informed financial decisions.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-100" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-1/3" />
                    <div className="h-6 bg-gray-100 rounded w-full" />
                    <div className="h-4 bg-gray-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, i) => (
                <ScrollReveal key={blog.id} delay={i * 100}>
                  <Link
                    to={`/blog/${blog.slug}`}
                    className="block rounded-2xl border border-gray-100 overflow-hidden shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-1 group bg-white"
                    data-testid={`blog-card-${blog.slug}`}
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3B82F6] bg-[#3B82F6]/5 px-2.5 py-1 rounded-full">
                          <Tag className="w-3 h-3" />
                          {blog.category}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {formatDate(blog.created_at)}
                        </span>
                      </div>
                      <h3 className="font-['Outfit'] text-lg font-semibold text-[#0B1F3A] mb-2 group-hover:text-[#3B82F6] transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                        {blog.excerpt}
                      </p>
                      <span className="text-sm font-medium text-[#3B82F6] flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read more <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          )}

          {!loading && blogs.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500">No articles yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
